/**
 * PSP/CRE Tool v3.0 - Default Scenario Templates
 * 
 * Provides pre-configured default scenarios for:
 * - Romanian Army Standard
 * - NATO Standard
 * - US Army Standard
 * 
 * Author: Dr. Tatar Radu MD
 * Date: November 20, 2025
 * Updated: December 23, 2024 - Fixed schema field names to match App.jsx
 * 
 * SCHEMA FIX:
 * - operations → operation_parameters
 * - medical → medical_configuration
 * - calculations → calculation_results
 */

/**
 * Get default scenario for specified standard
 * @param {string} standard - 'ROMANIAN', 'NATO', or 'US'
 * @returns {Object} Complete default scenario
 */
export function getDefaultScenario(standard = 'ROMANIAN') {
  const scenarios = {
    ROMANIAN: getRomanianDefault(),
    NATO: getNATODefault(),
    US: getUSDefault()
  };

  return scenarios[standard] || scenarios.ROMANIAN;
}

/**
 * Romanian Army Standard Default Scenario
 * Brigade-level mechanized force in high-intensity combat
 */
function getRomanianDefault() {
  return {
    // Tab 1: Force Composition
    // NOTE: forceBuilderResult is null — user must click "Build" in Tab 1.
    // All selector fields are pre-populated so Tab 1 opens with correct values.
    force: {
      echelon: 'BRIGADE',
      nationalStandard: 'ROMANIAN',
      numDivisions: 1,
      numBrigades: 1,
      buildMode: 'QUICK',
      divisionStandards: {},
      brigadeStandards: {},
      battalionWarningAccepted: false,
      configured: false,
      forceBuilderResult: null,

      brigade: {
        name: '1st Mechanized Brigade',
        strength: 4000,
        unitType: 'MECHANIZED',
        position: 1
      },

      medicalAssets: {
        ground: 16,
        air: 1
      },

      totalStrength: 4000
    },

    // Tab 2: Operations Configuration (FIXED: was "operations")
    operation_parameters: {
      operation_type: 'GROUND_COMBAT',
      operational_posture: 'ATTACK',
      duration_days: 30,
      phase_count: 1,
      
      phases: [
        {
          phase_number: 1,
          phase_name: 'Main Attack',
          start_day: 1,
          duration_days: 30,
          operational_posture: 'ATTACK',
          intensity: 'HIGH',
          terrain: 'MIXED',
          weather: 'CLEAR',
          enemy_strength: 'EQUAL',
          casualty_rate: 0.02  // 2% per day
        }
      ],
      
      environmental_factors: {
        terrain: 'MIXED',
        weather: 'CLEAR',
        season: 'SUMMER'
      }
    },

    // Tab 3: Medical Configuration (FIXED: was "medical")
    medical_configuration: {
      configured: true,
      mode: 'STANDARD',
      r3_accessible: true,  // ECHELON-DUAL: false = R3 hospital not in theatre

      // MTF Distances (Romanian brigade standard)
      mtf_distances: {
        role1_distance_km: 8,
        role2_distance_km: 20,
        role3_distance_km: 100,
        role4_distance_km: 500
      },
      
      // Medical Treatment Facilities
      mtfs: [
        {
          id: 'mtf_role1_1',
          role: 'ROLE_1',
          national_standard: 'ROMANIAN',
          personnel: {
            medical_officers: 2,
            nurses: 0,
            combat_medics: 20,
            support_staff: 8,
            total: 30
          },
          allocated_to: 'Battalion 1'
        },
        {
          id: 'mtf_role2e_1',
          role: 'ROLE_2_ENHANCED',
          national_standard: 'ROMANIAN',
          personnel: {
            medical_officers: 8,
            surgeons: 2,
            anesthesiologists: 1,
            nurses: 15,
            or_techs: 4,
            combat_medics: 30,
            support_staff: 15,
            total: 75
          },
          allocated_to: 'Brigade HQ'
        }
      ],
      
      // Evacuation Assets
      evacuation_assets: {
        ground: {
          light_ambulances: 10,
          armored_ambulances: 6
        },
        air: {
          iar330_puma: 1  // Romanian helicopter
        }
      },
      
      // No advanced staging by default
      use_advanced_staging: false,
      staging_facilities: [],
      helicopter_facilities: []
    },

    // Tab 4: Logistics (empty by default)
    logistics: null,

    // Tab 5: Calculations (FIXED: was "calculations")
    calculation_results: null,

    // Metadata
    metadata: {
      created: new Date().toISOString(),
      version: '3.0',
      template: 'ROMANIAN_BRIGADE_DEFAULT',
      description: 'Romanian mechanized brigade in high-intensity combat operation'
    }
  };
}

/**
 * NATO Standard Default Scenario
 * Brigade-level combined arms force in peace enforcement
 */
function getNATODefault() {
  return {
    force: {
      echelon: 'BRIGADE',
      nationalStandard: 'NATO',
      numDivisions: 1,
      numBrigades: 1,
      buildMode: 'QUICK',
      divisionStandards: {},
      brigadeStandards: {},
      battalionWarningAccepted: false,
      configured: false,
      forceBuilderResult: null,

      brigade: {
        name: 'NATO Brigade',
        strength: 5000,
        unitType: 'MECHANIZED',
        position: 1
      },

      medicalAssets: {
        ground: 20,
        air: 2
      },

      totalStrength: 5000
    },

    // Tab 2: Operations Configuration (FIXED: was "operations")
    operation_parameters: {
      operation_type: 'PEACE_ENFORCEMENT',
      operational_posture: 'DEFENSIVE',
      duration_days: 90,
      phase_count: 1,
      
      phases: [
        {
          phase_number: 1,
          phase_name: 'Stabilization',
          start_day: 1,
          duration_days: 90,
          operational_posture: 'DEFENSIVE',
          intensity: 'LOW',
          terrain: 'URBAN',
          weather: 'CLEAR',
          enemy_strength: 'WEAKER',
          casualty_rate: 0.005  // 0.5% per day
        }
      ],
      
      environmental_factors: {
        terrain: 'URBAN',
        weather: 'CLEAR',
        season: 'SUMMER'
      }
    },

    // Tab 3: Medical Configuration (FIXED: was "medical")
    medical_configuration: {
      configured: true,
      mode: 'STANDARD',
      r3_accessible: true,  // ECHELON-DUAL: false = R3 hospital not in theatre

      mtf_distances: {
        role1_distance_km: 10,
        role2_distance_km: 25,
        role3_distance_km: 120,
        role4_distance_km: 600
      },
      
      mtfs: [
        {
          id: 'mtf_role1_1',
          role: 'ROLE_1',
          national_standard: 'NATO',
          personnel: {
            medical_officers: 2,
            nurses: 0,
            combat_medics: 25,
            support_staff: 8,
            total: 35
          },
          allocated_to: 'Battalion 1'
        },
        {
          id: 'mtf_role2e_1',
          role: 'ROLE_2_ENHANCED',
          national_standard: 'NATO',
          personnel: {
            medical_officers: 10,
            surgeons: 3,
            anesthesiologists: 2,
            nurses: 20,
            or_techs: 6,
            combat_medics: 35,
            support_staff: 15,
            total: 91
          },
          allocated_to: 'Brigade HQ'
        }
      ],
      
      evacuation_assets: {
        ground: {
          light_ambulances: 12,
          armored_ambulances: 8
        },
        air: {
          uh60_blackhawk: 2
        }
      },
      
      use_advanced_staging: false,
      staging_facilities: [],
      helicopter_facilities: []
    },

    logistics: null,
    
    // Tab 5: Calculations (FIXED: was "calculations")
    calculation_results: null,

    metadata: {
      created: new Date().toISOString(),
      version: '3.0',
      template: 'NATO_BRIGADE_DEFAULT',
      description: 'NATO standard brigade in peace enforcement operation'
    }
  };
}

/**
 * US Army Standard Default Scenario
 * Brigade Combat Team in decisive action
 */
function getUSDefault() {
  return {
    force: {
      echelon: 'BRIGADE',
      nationalStandard: 'US',
      numDivisions: 1,
      numBrigades: 1,
      buildMode: 'QUICK',
      divisionStandards: {},
      brigadeStandards: {},
      battalionWarningAccepted: false,
      configured: false,
      forceBuilderResult: null,

      brigade: {
        name: '1st Brigade Combat Team',
        strength: 5000,
        unitType: 'ARMOR',
        position: 1
      },

      medicalAssets: {
        ground: 20,
        air: 2
      },

      totalStrength: 5000
    },

    // Tab 2: Operations Configuration (FIXED: was "operations")
    operation_parameters: {
      operation_type: 'GROUND_COMBAT',
      operational_posture: 'ATTACK',
      duration_days: 21,
      phase_count: 2,
      
      phases: [
        {
          phase_number: 1,
          phase_name: 'Penetration',
          start_day: 1,
          duration_days: 7,
          operational_posture: 'ATTACK',
          intensity: 'HIGH',
          terrain: 'OPEN',
          weather: 'CLEAR',
          enemy_strength: 'EQUAL',
          casualty_rate: 0.025  // 2.5% per day
        },
        {
          phase_number: 2,
          phase_name: 'Exploitation',
          start_day: 8,
          duration_days: 14,
          operational_posture: 'ATTACK',
          intensity: 'MEDIUM',
          terrain: 'MIXED',
          weather: 'CLEAR',
          enemy_strength: 'WEAKER',
          casualty_rate: 0.015  // 1.5% per day
        }
      ],
      
      environmental_factors: {
        terrain: 'OPEN',
        weather: 'CLEAR',
        season: 'SUMMER'
      }
    },

    // Tab 3: Medical Configuration (FIXED: was "medical")
    medical_configuration: {
      configured: true,
      mode: 'STANDARD',
      r3_accessible: true,  // ECHELON-DUAL: false = R3 hospital not in theatre

      mtf_distances: {
        role1_distance_km: 10,
        role2_distance_km: 30,
        role3_distance_km: 150,
        role4_distance_km: 800
      },
      
      mtfs: [
        {
          id: 'mtf_role1_1',
          role: 'ROLE_1',
          national_standard: 'US',
          personnel: {
            medical_officers: 2,
            nurses: 2,
            combat_medics: 30,
            support_staff: 10,
            total: 44
          },
          allocated_to: 'Battalion 1'
        },
        {
          id: 'mtf_role2e_1',
          role: 'ROLE_2_ENHANCED',
          national_standard: 'US',
          personnel: {
            medical_officers: 12,
            surgeons: 4,
            anesthesiologists: 2,
            nurses: 25,
            or_techs: 6,
            combat_medics: 40,
            support_staff: 15,
            total: 104
          },
          allocated_to: 'BCT HQ'
        }
      ],
      
      evacuation_assets: {
        ground: {
          light_ambulances: 15,
          armored_ambulances: 5
        },
        air: {
          uh60_blackhawk: 2
        }
      },
      
      use_advanced_staging: false,
      staging_facilities: [],
      helicopter_facilities: []
    },

    logistics: null,
    
    // Tab 5: Calculations (FIXED: was "calculations")
    calculation_results: null,

    metadata: {
      created: new Date().toISOString(),
      version: '3.0',
      template: 'US_BCT_DEFAULT',
      description: 'US Army Brigade Combat Team in decisive action'
    }
  };
}

/**
 * Get list of available templates
 * @returns {Array} Array of template descriptors
 */
export function getTemplateList() {
  return [
    {
      id: 'ROMANIAN',
      name: 'Romanian Brigade (High Intensity)',
      flag: '🇷🇴',
      description: 'Romanian mechanized brigade, 4000 personnel, 30-day high-intensity combat',
      echelon: 'BRIGADE',
      duration: 30,
      intensity: 'HIGH'
    },
    {
      id: 'NATO',
      name: 'NATO Brigade (Peace Enforcement)',
      flag: '🌐',
      description: 'NATO standard brigade, 5000 personnel, 90-day peace enforcement',
      echelon: 'BRIGADE',
      duration: 90,
      intensity: 'LOW'
    },
    {
      id: 'US',
      name: 'US BCT (Decisive Action)',
      flag: '🇺🇸',
      description: 'US Brigade Combat Team, 5000 personnel, 21-day offensive operation',
      echelon: 'BRIGADE',
      duration: 21,
      intensity: 'HIGH'
    }
  ];
}

// Named default export (fixes lint warning)
const defaultScenarios = { getDefaultScenario, getTemplateList };

export default defaultScenarios;
